
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

function BankStatement() {
    const API_URL = "https://eshaastra.in/api";

    // =========================================================
    // EMPTY ROW
    // =========================================================

    const emptyRow = {
        bank: "",
        date: new Date().toISOString().split("T")[0],
        party: {
            party_id: "",
            party_type: ""
        },
        credit_usd: "",
        credit_brl: "",
        debit_brl: "",
        remarks: "Done"
    };

    const createEmptyRows = (count = 10) =>
        Array.from({ length: count }, () => ({
            ...emptyRow,
            party: { ...emptyRow.party },
            id: crypto.randomUUID()
        }));

    // =========================================================
    // STATE
    // =========================================================

    const [rows, setRows] = useState(() => createEmptyRows());

    const [banks, setBanks] = useState([]);
    const [parties, setParties] = useState([]);
    const [bankStatements, setBankStatements] = useState([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchType, setSearchType] = useState("both");

    // Searchable popup state
    const popupRef = useRef(null);

    const [popup, setPopup] = useState({
        show: false,
        row: -1,
        field: "",
        x: 0,
        y: 0,
        data: [],
        searchText: ""
    });

    // =========================================================
    // LOAD INITIAL DATA
    // =========================================================

    useEffect(() => {
        loadBanks();
        loadBankStatements();
    }, []);

    // =========================================================
    // GET BANKS
    // =========================================================

    const loadBanks = async () => {
        try {
            const response = await axios.get(`${API_URL}/banks`);

            const bankData = response.data.data || [];

            setBanks(bankData);

            // Load parties after banks so banks can also be
            // included in the All Parties dropdown.
            await loadParties(bankData);

        } catch (error) {
            console.error("Bank API error:", error);
            toast.error("Failed to load banks");
        }
    };

    // =========================================================
    // GET ALL PARTIES
    // =========================================================

    const loadParties = async (bankData = []) => {
        try {
            const [
                suppliers,
                buyers,
                transporters,
                ports,
                materials,
                redex,
                despatcho
            ] = await Promise.all([
                axios.get(`${API_URL}/supplier`),
                axios.get(`${API_URL}/buyers`),
                axios.get(`${API_URL}/transporter`),
                axios.get(`${API_URL}/ports`),
                axios.get(`${API_URL}/materials`),
                axios.get(`${API_URL}/redex`),
                axios.get(`${API_URL}/despatcho`)
            ]);

            const allParties = [
                ...(suppliers.data.data || []).map(item => ({
                    ...item,
                    party_type: "Supplier"
                })),

                ...(buyers.data.data || []).map(item => ({
                    ...item,
                    party_type: "Buyer"
                })),

                ...(transporters.data.data || []).map(item => ({
                    ...item,
                    party_type: "Transporter"
                })),

                ...(ports.data.data || []).map(item => ({
                    ...item,
                    party_type: "Port"
                })),

                ...(materials.data.data || []).map(item => ({
                    ...item,
                    party_type: "Material"
                })),

                ...(redex.data.data || []).map(item => ({
                    ...item,
                    party_type: "Redex"
                })),

                ...(despatcho.data.data || []).map(item => ({
                    ...item,
                    party_type: "Despatcho"
                }))
            ];

            // Include banks in All Parties
            const bankParties = bankData.map(bank => ({
                ...bank,
                party_type: "Bank"
            }));

            setParties([
                ...allParties,
                ...bankParties
            ]);

        } catch (error) {
            console.error("Party API error:", error);
            toast.error("Failed to load parties");
        }
    };

    // =========================================================
    // GET BANK STATEMENTS
    // =========================================================

    const loadBankStatements = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${API_URL}/bank-statements`
            );

            setBankStatements(response.data.data || []);

        } catch (error) {
            console.error("Statement API error:", error);
            toast.error("Failed to load bank statements");

        } finally {
            setLoading(false);
        }
    };

    // =========================================================
    // HANDLE INPUT CHANGE
    // =========================================================

    const handleChange = (index, field, value) => {
        setRows(prevRows => {
            const updatedRows = [...prevRows];

            updatedRows[index] = {
                ...updatedRows[index],
                [field]: value
            };

            return updatedRows;
        });
    };

    // =========================================================
    // BANK CHANGE
    // =========================================================

    const handleBankChange = (index, value) => {
        handleChange(index, "bank", value);
    };

    // =========================================================
    // PARTY CHANGE
    // =========================================================

    const handlePartyChange = (index, value) => {
        const selectedParty = parties.find(
            item => item._id === value
        );

        setRows(prevRows => {
            const updatedRows = [...prevRows];

            updatedRows[index] = {
                ...updatedRows[index],
                party: {
                    party_id: selectedParty?._id || "",
                    party_type: selectedParty?.party_type || ""
                }
            };

            return updatedRows;
        });
    };

    // =========================================================
    // OPEN SEARCHABLE POPUP
    // =========================================================

    const openPopup = (e, rowIndex, field) => {
        e.preventDefault();
        e.stopPropagation();

        const data = field === "bank" ? banks : parties;

        if (!data || data.length === 0) {
            toast.info(
                field === "bank"
                    ? "No banks available"
                    : "No parties available"
            );
            return;
        }

        const rect = e.currentTarget.getBoundingClientRect();
        const popupWidth = 280;
        const popupHeight = 310;

        const x = Math.max(
            5,
            Math.min(
                rect.left,
                window.innerWidth - popupWidth - 5
            )
        );

        // Keep popup within the visible screen.
        let y = rect.bottom;

        if (y + popupHeight > window.innerHeight) {
            y = rect.top - popupHeight;
        }

        y = Math.max(
            5,
            Math.min(y, window.innerHeight - popupHeight - 5)
        );

        setPopup({
            show: true,
            row: rowIndex,
            field,
            x,
            y,
            data,
            searchText: ""
        });
    };

    // =========================================================
    // CLOSE POPUP
    // =========================================================

    const closePopup = () => {
        setPopup(prev => ({
            ...prev,
            show: false,
            searchText: ""
        }));
    };

    // =========================================================
    // SELECT POPUP ITEM
    // =========================================================

    const selectPopupItem = (item) => {
        const { row, field } = popup;

        if (field === "bank") {
            handleBankChange(row, item._id);
        }

        if (field === "party") {
            setRows(prevRows => {
                const updatedRows = [...prevRows];

                updatedRows[row] = {
                    ...updatedRows[row],
                    party: {
                        party_id: item._id,
                        party_type: item.party_type
                    }
                };

                return updatedRows;
            });
        }

        closePopup();
    };

    // =========================================================
    // POPUP SEARCH
    // =========================================================

    const handlePopupSearch = (e) => {
        const value = e.target.value;

        setPopup(prev => ({
            ...prev,
            searchText: value
        }));
    };

    // =========================================================
    // FILTER POPUP DATA
    // =========================================================

    const filteredPopupData = useMemo(() => {
        const search = popup.searchText.toLowerCase().trim();

        if (!search) {
            return popup.data;
        }

        return popup.data.filter(item => {
            const name = String(item.name || "").toLowerCase();
            const type = String(item.party_type || "").toLowerCase();

            return (
                name.includes(search) ||
                type.includes(search)
            );
        });
    }, [popup.data, popup.searchText]);

    // =========================================================
    // CLICK OUTSIDE / ESCAPE TO CLOSE POPUP
    // =========================================================

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target)
            ) {
                closePopup();
            }
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                closePopup();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    // =========================================================
    // ADD ROW
    // =========================================================

    const addRow = () => {
        setRows(prev => [
            ...prev,
            {
                ...emptyRow,
                party: { ...emptyRow.party },
                id: crypto.randomUUID()
            }
        ]);
    };

    // =========================================================
    // REMOVE ROW
    // =========================================================

    const removeRow = (index) => {
        if (rows.length === 1) {
            toast.warning("At least one row is required");
            return;
        }

        setRows(prev => prev.filter((_, i) => i !== index));
    };

    // =========================================================
    // CHECK ROW STARTED
    // =========================================================

    const isRowStarted = (row) => {
        return (
            row.bank ||
            row.party?.party_id ||
            row.credit_usd !== "" ||
            row.credit_brl !== "" ||
            row.debit_brl !== "" ||
            (row.remarks && row.remarks.trim() !== "")
        );
    };

    // =========================================================
    // CHECK ROW COMPLETE
    // =========================================================

    const isRowComplete = (row) => {
        return (
            row.bank &&
            row.date &&
            row.party?.party_id &&
            row.party?.party_type &&
            row.credit_usd !== "" &&
            row.credit_brl !== "" &&
            row.debit_brl !== "" &&
            row.remarks?.trim() !== ""
        );
    };

    // =========================================================
    // SAVE BANK STATEMENTS
    // =========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        const startedRows = rows.filter(row => isRowStarted(row));

        if (startedRows.length === 0) {
            toast.warning("Please enter at least one bank statement.");
            return;
        }

        const incompleteRows = startedRows.filter(
            row => !isRowComplete(row)
        );

        if (incompleteRows.length > 0) {
            toast.error(
                `Please complete all fields in ${incompleteRows.length} row(s).`
            );
            return;
        }

        const entries = startedRows.filter(
            row => isRowComplete(row)
        );

        setSaving(true);

        try {
            await axios.post(
                `${API_URL}/bank-statements`,
                { entries }
            );

            toast.success(
                `${entries.length} bank statement(s) saved successfully`
            );

            setRows(createEmptyRows(10));

            await loadBankStatements();

        } catch (error) {
            console.error("Save error:", error);

            toast.error(
                error.response?.data?.message ||
                "Failed to save bank statement"
            );

        } finally {
            setSaving(false);
        }
    };

    // =========================================================
    // 24-HOUR LOCK CHECK
    // =========================================================

    const isLocked = (statement) => {
        if (!statement.createdAt) {
            return false;
        }

        const created = new Date(statement.createdAt).getTime();
        const hours = (Date.now() - created) / (1000 * 60 * 60);

        return hours >= 24;
    };

    // =========================================================
    // DELETE STATEMENT
    // =========================================================

    const deleteStatement = async (statement) => {
        if (isLocked(statement)) {
            toast.error("This entry is locked after 24 hours.");
            return;
        }

        const result = await Swal.fire({
            title: "Delete Statement?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete"
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await axios.delete(
                `${API_URL}/bank-statements/${statement._id}`
            );

            toast.success("Statement deleted");

            await loadBankStatements();

        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Delete failed");
        }
    };

    // =========================================================
    // SEARCH STATEMENTS
    // =========================================================

    const filteredStatements = useMemo(() => {
        const search = searchText.toLowerCase().trim();

        if (!search) {
            return bankStatements;
        }

        return bankStatements.filter(item => {
            const bankName = String(
                item.bank?.name || item.bank || ""
            ).toLowerCase();

            const partyName = String(
                item.party?.name || item.party_name || ""
            ).toLowerCase();

            if (searchType === "bank") {
                return bankName.includes(search);
            }

            if (searchType === "party") {
                return partyName.includes(search);
            }

            return (
                bankName.includes(search) ||
                partyName.includes(search)
            );
        });
    }, [bankStatements, searchText, searchType]);

    // =========================================================
    // TOTALS
    // =========================================================

    const totals = useMemo(() => {
        return filteredStatements.reduce(
            (total, item) => {
                total.credit_usd += Number(item.credit_usd || 0);
                total.credit_brl += Number(item.credit_brl || 0);
                total.debit_brl += Number(item.debit_brl || 0);

                return total;
            },
            {
                credit_usd: 0,
                credit_brl: 0,
                debit_brl: 0
            }
        );
    }, [filteredStatements]);

    // =========================================================
    // DATATABLE COLUMNS
    // =========================================================

    const columns = [
        {
            name: "#",
            width: "60px",
            cell: (row, index) => index + 1
        },
        {
            name: "BANK",
            selector: row =>
                row.bank?.name || row.bank || "-",
            sortable: true
        },
        {
            name: "DATE",
            selector: row => {
                if (!row.date) return "-";

                return new Date(row.date).toLocaleDateString("en-GB");
            },
            sortable: true
        },
        {
            name: "ALL PARTIES",
            selector: row =>
                row.party?.name || row.party_name || "-",
            sortable: true
        },
        {
            name: "CREDIT in USD",
            right: true,
            selector: row =>
                Number(row.credit_usd || 0).toFixed(3)
        },
        {
            name: "CREDIT(R$)",
            right: true,
            selector: row =>
                Number(row.credit_brl || 0).toFixed(3)
        },
        {
            name: "DEBIT(R$)",
            right: true,
            selector: row =>
                Number(row.debit_brl || 0).toFixed(3)
        },
        {
            name: "REMARKS",
            selector: row => row.remarks || "-"
        },
        {
            name: "ACTION",
            width: "120px",
            cell: row => {
                const locked = isLocked(row);

                return (
                    <div className="d-flex gap-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={locked}
                            title="Edit"
                        >
                            <i className="bi bi-pencil"></i>
                        </button>

                        <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={locked}
                            onClick={() => deleteStatement(row)}
                            title="Delete"
                        >
                            <i className="bi bi-trash"></i>
                        </button>

                        {locked && (
                            <span
                                className="text-danger"
                                title="Locked after 24 hours"
                            >
                                🔒
                            </span>
                        )}
                    </div>
                );
            }
        }
    ];

    // =========================================================
    // RENDER
    // =========================================================

    return (
        <div className="container-fluid">

            {/* HEADER AND SEARCH */}

            <div className="card shadow-sm border-0 mb-3">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="bi bi-bank me-2"></i>
                        Bank Summary
                    </h5>
                </div>

                <div className="card-body">
                    <div className="row g-2 align-items-center">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search Bank / Party"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </div>

                        <div className="col-md-6">
                            <div className="btn-group">
                                {["bank", "party", "both"].map(type => (
                                    <button
                                        key={type}
                                        type="button"
                                        className={`btn ${
                                            searchType === type
                                                ? "btn-primary"
                                                : "btn-outline-primary"
                                        }`}
                                        onClick={() => setSearchType(type)}
                                    >
                                        {type === "bank"
                                            ? "Bank"
                                            : type === "party"
                                                ? "All Party"
                                                : "Both"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ENTRY FORM */}

            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-dark text-white">
                    <strong>Add Bank Statement</strong>
                </div>

                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="table-responsive">
                            <table className="table table-bordered table-sm align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>BANK</th>
                                        <th>DATE</th>
                                        <th>ALL PARTIES</th>
                                        <th>CREDIT in USD</th>
                                        <th>CREDIT(R$)</th>
                                        <th>DEBIT(R$)</th>
                                        <th>REMARKS</th>
                                        <th>ACTION</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {rows.map((row, index) => {
                                        const started = isRowStarted(row);
                                        const complete = isRowComplete(row);

                                        const selectedParty = parties.find(
                                            party =>
                                                party._id === row.party?.party_id &&
                                                party.party_type === row.party?.party_type
                                        );

                                        const selectedBank = banks.find(
                                            bank => bank._id === row.bank
                                        );

                                        return (
                                            <tr
                                                key={row.id}
                                                className={
                                                    complete
                                                        ? "table-success"
                                                        : started
                                                            ? "table-warning"
                                                            : ""
                                                }
                                            >
                                                <td>{index + 1}</td>

                                                {/* BANK SEARCH INPUT */}

                                                <td style={{ minWidth: "160px" }}>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder={
                                                            banks.length === 0
                                                                ? "No banks available"
                                                                : "Search Bank..."
                                                        }
                                                        value={selectedBank?.name || ""}
                                                        onClick={e => {
                                                            if (banks.length > 0) {
                                                                openPopup(e, index, "bank");
                                                            }
                                                        }}
                                                        readOnly
                                                        disabled={banks.length === 0}
                                                        style={{
                                                            cursor: banks.length > 0
                                                                ? "pointer"
                                                                : "not-allowed",
                                                            backgroundColor: "#f8f9fa"
                                                        }}
                                                    />
                                                </td>

                                                {/* DATE */}

                                                <td>
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={row.date}
                                                        onChange={e =>
                                                            handleChange(
                                                                index,
                                                                "date",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {/* ALL PARTIES SEARCH INPUT */}

                                                <td style={{ minWidth: "220px" }}>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder={
                                                            parties.length === 0
                                                                ? "No parties available"
                                                                : "Search Party..."
                                                        }
                                                        value={
                                                            selectedParty
                                                                ? `${selectedParty.name} (${selectedParty.party_type})`
                                                                : ""
                                                        }
                                                        onClick={e => {
                                                            if (parties.length > 0) {
                                                                openPopup(e, index, "party");
                                                            }
                                                        }}
                                                        readOnly
                                                        disabled={parties.length === 0}
                                                        style={{
                                                            cursor: parties.length > 0
                                                                ? "pointer"
                                                                : "not-allowed",
                                                            backgroundColor: "#f8f9fa"
                                                        }}
                                                    />
                                                </td>

                                                {/* CREDIT USD */}

                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        className="form-control form-control-sm text-end"
                                                        placeholder="0.000"
                                                        value={row.credit_usd}
                                                        onChange={e =>
                                                            handleChange(
                                                                index,
                                                                "credit_usd",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {/* CREDIT BRL */}

                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        className="form-control form-control-sm text-end"
                                                        placeholder="0.000"
                                                        value={row.credit_brl}
                                                        onChange={e =>
                                                            handleChange(
                                                                index,
                                                                "credit_brl",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {/* DEBIT BRL */}

                                                <td>
                                                    <input
                                                        type="number"
                                                        step="0.001"
                                                        min="0"
                                                        className="form-control form-control-sm text-end"
                                                        placeholder="0.000"
                                                        value={row.debit_brl}
                                                        onChange={e =>
                                                            handleChange(
                                                                index,
                                                                "debit_brl",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {/* REMARKS */}

                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        placeholder="Remarks"
                                                        value={row.remarks}
                                                        onChange={e =>
                                                            handleChange(
                                                                index,
                                                                "remarks",
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </td>

                                                {/* REMOVE ROW */}

                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => removeRow(index)}
                                                        disabled={rows.length === 1}
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* FORM BUTTONS */}

                        <div className="d-flex justify-content-between mt-3">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={addRow}
                            >
                                <i className="bi bi-plus-circle me-1"></i>
                                Add Row
                            </button>

                            <button
                                type="submit"
                                className="btn btn-success"
                                disabled={saving || loading}
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-save me-2"></i>
                                        Save All Entries
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* WARNING */}

            <div className="alert alert-warning">
                ⚠️ <strong>Warning:</strong>{" "}
                Entries after 24 hours will be locked
                and cannot be updated again.
            </div>

            {/* TOTALS */}

            <div className="card shadow-sm border-0 mb-3">
                <div className="card-body">
                    <div className="row text-center">
                        <div className="col-md-4">
                            <strong>Total Credit USDT</strong>
                            <div className="fs-5 text-success">
                                {totals.credit_usd.toFixed(2)}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <strong>Total Credit Rupee</strong>
                            <div className="fs-5 text-success">
                                {totals.credit_brl.toFixed(2)}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <strong>Total Debit</strong>
                            <div className="fs-5 text-danger">
                                {totals.debit_brl.toFixed(2)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* BANK STATEMENTS TABLE */}

            <div className="card shadow-sm border-0">
                <div className="card-header bg-success text-white">
                    <div className="d-flex justify-content-between">
                        <strong>Bank Statements</strong>

                        <span>
                            Search Result: {filteredStatements.length}
                        </span>
                    </div>
                </div>

                <div className="card-body p-0">
                    <DataTable
                        columns={columns}
                        data={filteredStatements}
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[25, 50, 100, 200]}
                        progressPending={loading}
                        highlightOnHover
                        striped
                        dense
                        responsive={false}
                        noDataComponent="No bank statements found"
                    />
                </div>
            </div>

            {/* =====================================================
                SEARCHABLE DROPDOWN POPUP
            ====================================================== */}

            {popup.show && popup.data.length > 0 && (
                <div
                    ref={popupRef}
                    className="popup-select"
                    style={{
                        position: "fixed",
                        top: popup.y,
                        left: popup.x,
                        zIndex: 99999,
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        width: "280px",
                        maxHeight: "300px",
                        display: "flex",
                        flexDirection: "column"
                    }}
                >
                    {/* POPUP SEARCH INPUT */}

                    <div
                        style={{
                            padding: "8px",
                            borderBottom: "1px solid #eee"
                        }}
                    >
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={
                                popup.field === "bank"
                                    ? "Search Bank..."
                                    : "Search All Parties..."
                            }
                            value={popup.searchText}
                            onChange={handlePopupSearch}
                            autoFocus
                        />
                    </div>

                    {/* POPUP ITEMS */}

                    <div
                        style={{
                            overflowY: "auto",
                            maxHeight: "250px",
                            padding: "4px 0"
                        }}
                    >
                        {filteredPopupData.length > 0 ? (
                            filteredPopupData.map((item, idx) => (
                                <div
                                    key={`${item._id}-${item.party_type || "bank"}-${idx}`}
                                    className="popup-item"
                                    onClick={() => selectPopupItem(item)}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: "13px",
                                        borderBottom: "1px solid #f5f5f5"
                                    }}
                                >
                                    {item.name || "Unnamed"}

                                    {popup.field === "party" && (
                                        <span className="text-muted ms-1">
                                            ({item.party_type})
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div
                                style={{
                                    padding: "16px",
                                    textAlign: "center",
                                    color: "#666",
                                    fontSize: "13px"
                                }}
                            >
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default BankStatement;
